import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link, useLocation } from 'react-router'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuChevron,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarResizer,
  SidebarSeparator,
  useSidebar,
} from 'tailwind-v4/components/sidebar'
import { Button } from 'tailwind-v4/components/button'
import { Separator } from 'tailwind-v4/components/separator'
import { Skeleton } from 'tailwind-v4/components/skeleton'
import { cn } from 'tailwind-v4/lib/utils'
import {
  ArrowLeft,
  ChevronRight,
  ChevronsLeft,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import { useWebHaptics } from 'web-haptics/react'
import { useUpdateAvailable } from '@/components/update-context'
import { preloadRouteByHref } from '@/routes'
import { openCreateReviewPage } from '@/components/Reviews/create-review-page-dialog'
import {
  isNavItemVisible,
  openSectionIdForPath,
  resolveHref,
  resolveLabel,
  type NavContext,
  type NavItem,
} from './config'

/**
 * Generic sidebar shell shared by the customer-facing app (`NewSidebarShell`)
 * and the admin panel (`AdminSidebarShell`). Owns the drill-in state machine,
 * the `Sidebar` chrome (collapse control + resizer), and the persistent
 * "update available" affordance. Everything app/admin-specific — the header
 * content, the footer user block, and the badge counts — is injected via props,
 * so this file has no knowledge of orgs, venues, or admin.
 */

function isActive(
  pathname: string,
  href: string | undefined,
  mode: 'prefix' | 'exact' = 'prefix'
): boolean {
  if (!href) return false
  if (href === pathname) return true
  if (mode === 'exact') return false
  return pathname.startsWith(`${href}/`)
}

/** Count indicator for a nav row (e.g. unhandled enquiries), right-aligned.
 *  A plain destructive-red number — NO circle/pill background. */
function NavBadge({ count }: { count: number }) {
  return (
    <span className="ml-auto self-center text-xs font-semibold text-destructive">
      {count > 99 ? '99+' : count}
    </span>
  )
}

/** Small text badge beside a nav label — `new` (info tint) flags a newer
 *  replacement, `degraded` (warning tint) flags a legacy item being phased out,
 *  `stampede` (primary tint) marks a surface only Stampede staff can see. */
function NavTextBadge({
  label,
  tone,
}: {
  label: string
  tone: 'new' | 'degraded' | 'stampede'
}) {
  const TONE = {
    new: 'border border-info/30 bg-info/10 text-info',
    degraded: 'border border-warning/30 bg-warning/10 text-warning',
    // Solid and borderless rather than a tinted outline, so a staff-only row
    // reads at a glance instead of blending into the tinted badges beside it.
    // `--brand-warm` with the same literal fallback the day-planner now-line
    // uses, which is the existing precedent for orange-with-white-text.
    stampede: 'bg-[var(--brand-warm,#ff6b4a)] text-white',
  } as const

  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-1.5 py-px text-xs font-medium',
        TONE[tone]
      )}
    >
      {label}
    </span>
  )
}

/**
 * Placeholder rows shown while the access subject resolves. Matches the real
 * row's height and rhythm so the sidebar does not shift when the items land.
 */
function NavListSkeleton({ count }: { count: number }) {
  // Cap it: a long nav would otherwise render a wall of grey.
  const rows = Math.min(Math.max(count, 3), 8)
  return (
    <SidebarMenu
      aria-hidden
      className="gap-[1px] group-data-[collapsible=icon]:gap-0"
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex h-8 items-center gap-2 px-2">
          <Skeleton className="size-4 shrink-0 rounded-md" />
          <Skeleton className="h-3 w-24 group-data-[collapsible=icon]:hidden" />
        </div>
      ))}
    </SidebarMenu>
  )
}

function NavList({
  items,
  ctx,
  badges,
  onDrillInto,
}: {
  items: NavItem[]
  ctx: NavContext
  badges?: Record<string, number>
  onDrillInto?: (item: NavItem) => void
}) {
  const { pathname } = useLocation()
  // Capability gates fail closed, so until the access subject resolves the
  // role-gated sections are all hidden. Rendering the list anyway would show a
  // half-empty sidebar for the length of one request and then pop the rest in.
  // A skeleton is the honest representation of "we don't know yet" and it holds
  // the layout steady.
  if (ctx.access && !ctx.access.isResolved) {
    return <NavListSkeleton count={items.length} />
  }
  const visibleItems = items.filter((item) => isNavItemVisible(item, ctx))
  return (
    <SidebarMenu className="gap-[1px] group-data-[collapsible=icon]:gap-0">
      {visibleItems.map((item, i) => {
        const href = resolveHref(item, ctx)
        const label = resolveLabel(item, ctx)
        const hasChildren = Boolean(item.children?.length)
        const isAction = Boolean(item.action)
        const showSeparator = item.separatorBefore && i > 0
        // Inline sections (e.g. Inbox) expand in place instead of drilling
        // into a replacement panel — same accordion as drilled sub-navs.
        if (hasChildren && item.inlineChildren) {
          return (
            <Fragment key={item.id}>
              {showSeparator ? (
                <Separator className="my-1.5 bg-border" />
              ) : null}
              <SubNavAccordion item={item} ctx={ctx} badges={badges} />
            </Fragment>
          )
        }
        const handleActionClick = () => {
          if (item.action === 'create-review-page') openCreateReviewPage()
        }
        const handleClick = hasChildren
          ? (e: React.MouseEvent) => {
              e.preventDefault()
              onDrillInto?.(item)
            }
          : isAction
            ? handleActionClick
            : undefined
        return (
          <Fragment key={item.id}>
            {showSeparator ? <Separator className="my-1.5 bg-border" /> : null}
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isActive(pathname, href, item.activeMatch)}
                tooltip={label}
                render={
                  hasChildren || isAction || !href ? (
                    <button type="button" />
                  ) : item.external ? (
                    <a href={href} target="_blank" rel="noreferrer" />
                  ) : (
                    <Link to={href} />
                  )
                }
                onClick={handleClick}
                onMouseEnter={() => {
                  if (!hasChildren && !item.external) preloadRouteByHref(href)
                }}
                className="gap-3 py-2 px-3 h-auto"
              >
                <item.icon />
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{label}</span>
                  {/* A flat leaf item (no children) never hit any of the
                      three existing `.badge` render sites — those are all
                      for group headers / child / grandchild rows. Knowledge
                      Base is the first plain top-level item to need one. */}
                  {item.badge ? (
                    <NavTextBadge
                      label={item.badge.label}
                      tone={item.badge.tone}
                    />
                  ) : null}
                </span>
                {badges?.[item.id] ? (
                  <NavBadge count={badges[item.id]} />
                ) : null}
                {hasChildren ? (
                  // With a badge present, drop the chevron's own `ml-auto` —
                  // two auto-margins split the row's free space, floating the
                  // badge to the middle. The badge's `ml-auto` pushes the pair
                  // right; the chevron then just follows on the gap.
                  <SidebarMenuChevron
                    className={badges?.[item.id] ? 'ml-0' : undefined}
                  />
                ) : null}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </Fragment>
        )
      })}
    </SidebarMenu>
  )
}

/**
 * A sub-nav child that itself has children (e.g. the SnapGuard / Hardware
 * groups inside Wi-Fi, or Bookings → Main Settings). Renders as a one-level
 * accordion: from OUTSIDE the group, clicking the parent navigates to its
 * first visible grandchild, which expands the group (the approved "expand +
 * go to overview" behaviour). Once the active route is INSIDE the group,
 * the parent row becomes a pure toggle — click to collapse, click to
 * re-expand — since navigating again would be a no-op and operators need a
 * way to fold a long section list away. Navigating in or out of the group
 * re-derives the open state from the route.
 */
function SubNavAccordion({
  item,
  ctx,
  badges,
}: {
  item: NavItem
  ctx: NavContext
  badges?: Record<string, number>
}) {
  const { pathname } = useLocation()
  const grandchildren = (item.children ?? []).filter((child) =>
    isNavItemVisible(child, ctx)
  )
  const label = resolveLabel(item, ctx)
  const ownHref = resolveHref(item, ctx)
  const firstHref = grandchildren.length
    ? resolveHref(grandchildren[0], ctx)
    : ownHref
  const routeOpen =
    isActive(pathname, ownHref, item.activeMatch) ||
    grandchildren.some((gc) =>
      isActive(pathname, resolveHref(gc, ctx), gc.activeMatch)
    )
  // Manual fold while inside the group; cleared whenever the route
  // enters/leaves the group so navigation always re-derives.
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false)
  useEffect(() => {
    setManuallyCollapsed(false)
  }, [routeOpen])
  const open = routeOpen && !manuallyCollapsed

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        // Group header, not a destination in its own right — the active
        // grandchild carries the highlight, so the header never double-lights.
        isActive={false}
        tooltip={label}
        render={firstHref ? <Link to={firstHref} /> : <button type="button" />}
        onClick={(e: React.MouseEvent) => {
          if (!routeOpen) return
          // Already inside the group: don't re-navigate, just fold/unfold.
          e.preventDefault()
          setManuallyCollapsed((prev) => !prev)
        }}
        onMouseEnter={() => {
          preloadRouteByHref(firstHref)
        }}
        className={cn(
          'gap-3 py-2 px-3 h-auto',
          // Destructive-tone accordion parent (e.g. Archived) reads red, like
          // a destructive leaf row does.
          item.tone === 'destructive' &&
            'text-destructive hover:text-destructive [&_svg]:text-destructive data-active:text-destructive data-active:[&_svg]:text-destructive'
        )}
      >
        <item.icon />
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label}</span>
          {/* The third and last place `badge` has to be rendered: a group header
              is neither a child row nor a grandchild row, so Wallets declared a
              badge and showed nothing in either state. Not gated on `open` —
              that gate belongs to the numeric count below, which the child row
              takes over when expanded. A staff-only marker has to be visible
              precisely when the group is shut. The label truncates before the
              badge does, since the badge is the part that must survive. */}
          {item.badge ? (
            <NavTextBadge label={item.badge.label} tone={item.badge.tone} />
          ) : null}
        </span>
        <span className="ml-auto flex items-center gap-3">
          {/* Only when collapsed — when open, the child row shows the count. */}
          {!open && badges?.[item.id] ? (
            <NavBadge count={badges[item.id]} />
          ) : null}
          <ChevronRight
            aria-hidden
            strokeWidth={2.5}
            className={`size-3.5 opacity-60 transition-transform duration-100 ease-out ${
              open ? 'rotate-90' : ''
            }`}
          />
        </span>
      </SidebarMenuButton>
      {open ? (
        <SidebarMenuSub>
          {grandchildren.map((gc) => {
            const href = resolveHref(gc, ctx)
            return (
              <SidebarMenuSubItem key={gc.id}>
                <SidebarMenuSubButton
                  isActive={isActive(pathname, href, gc.activeMatch)}
                  render={
                    gc.action ? (
                      <button type="button" />
                    ) : href ? (
                      gc.external ? (
                        <a href={href} target="_blank" rel="noreferrer" />
                      ) : (
                        <Link to={href} />
                      )
                    ) : undefined
                  }
                  onClick={
                    gc.action === 'create-review-page'
                      ? () => {
                          openCreateReviewPage()
                        }
                      : undefined
                  }
                  onMouseEnter={() => {
                    if (!gc.external && !gc.action) preloadRouteByHref(href)
                  }}
                >
                  <gc.icon />
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{resolveLabel(gc, ctx)}</span>
                    {/* Rendered here as well as on child rows: `badge` was
                        silently ignored at this depth, so declaring one on a
                        grandchild did nothing at all. */}
                    {gc.badge ? (
                      <NavTextBadge
                        label={gc.badge.label}
                        tone={gc.badge.tone}
                      />
                    ) : null}
                  </span>
                  {badges?.[gc.id] ? <NavBadge count={badges[gc.id]} /> : null}
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            )
          })}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  )
}

function SubNav({
  section,
  ctx,
  badges,
  onBack,
}: {
  section: NavItem
  ctx: NavContext
  badges?: Record<string, number>
  onBack: () => void
}) {
  const { pathname } = useLocation()
  const children = (section.children ?? []).filter((child) =>
    isNavItemVisible(child, ctx)
  )

  // Opening a section prefetches its sub-items' route chunks (deferred so the
  // current view keeps priority) — clicking any of them then renders instantly
  // instead of waiting on a cold code-split chunk.
  useEffect(() => {
    const id = window.setTimeout(() => {
      for (const child of section.children ?? []) {
        if (child.external || !isNavItemVisible(child, ctx)) continue
        preloadRouteByHref(resolveHref(child, ctx))
      }
    }, 150)
    return () => {
      window.clearTimeout(id)
    }
  }, [section, ctx])

  return (
    <div className="flex flex-col gap-0 px-2 pt-px pb-2">
      <Button
        variant="ghost"
        size="lg"
        onClick={onBack}
        className="relative h-auto w-full justify-center py-2 font-medium hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]"
      >
        <ArrowLeft
          strokeWidth={2.5}
          aria-hidden
          className="absolute left-2 size-4 text-[var(--muted-foreground)]"
        />
        <span>{resolveLabel(section, ctx)}</span>
      </Button>
      <SidebarMenu className="gap-[1px]">
        {children.map((child, index) => {
          const href = resolveHref(child, ctx)
          const label = resolveLabel(child, ctx)
          // Never a divider before the first item — it would sit directly
          // under the back button.
          const showSeparator = index > 0 && child.separatorBefore
          // A child with its own visible children renders as a one-level
          // accordion (e.g. the SnapGuard / Hardware groups); otherwise it's
          // a leaf link.
          const hasGrandchildren = (child.children ?? []).some((gc) =>
            isNavItemVisible(gc, ctx)
          )
          return (
            <Fragment key={child.id}>
              {showSeparator ? (
                <Separator className="my-1.5 bg-border" />
              ) : null}
              {hasGrandchildren ? (
                <SubNavAccordion item={child} ctx={ctx} badges={badges} />
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActive(pathname, href, child.activeMatch)}
                    tooltip={label}
                    render={
                      child.action ? (
                        <button type="button" />
                      ) : href ? (
                        child.external ? (
                          <a href={href} target="_blank" rel="noreferrer" />
                        ) : (
                          <Link to={href} />
                        )
                      ) : undefined
                    }
                    onClick={
                      child.action === 'create-review-page'
                        ? () => {
                            openCreateReviewPage()
                          }
                        : undefined
                    }
                    onMouseEnter={() => {
                      if (!child.external && !child.action) {
                        preloadRouteByHref(href)
                      }
                    }}
                    className={cn(
                      'gap-3 py-2 px-3 h-auto',
                      child.tone === 'destructive' &&
                        'text-destructive hover:text-destructive [&_svg]:text-destructive data-active:text-destructive data-active:[&_svg]:text-destructive'
                    )}
                  >
                    <child.icon />
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{label}</span>
                      {child.badge ? (
                        <NavTextBadge
                          label={child.badge.label}
                          tone={child.badge.tone}
                        />
                      ) : null}
                    </span>
                    {badges?.[child.id] ? (
                      <NavBadge count={badges[child.id]} />
                    ) : null}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </Fragment>
          )
        })}
      </SidebarMenu>
    </div>
  )
}

export function SidebarShellCore({
  primary,
  secondary,
  ctx,
  badges,
  header,
  footer,
}: {
  primary: NavItem[]
  secondary: NavItem[]
  ctx: NavContext
  badges?: Record<string, number>
  /** Left-hand content of the sidebar header (brand / switcher). The generic
   *  collapse control is rendered by the core to its right. */
  header: ReactNode
  /** The user block rendered at the bottom of the footer, below the generic
   *  "update available" affordance. */
  footer: ReactNode
}) {
  const { pathname } = useLocation()
  const { trigger } = useWebHaptics()
  const { toggleSidebar } = useSidebar()
  const {
    updateAvailable,
    applyUpdate,
    applying: applyingUpdate,
  } = useUpdateAvailable()

  const [openSectionId, setOpenSectionId] = useState<string | null>(null)
  // When set, the user explicitly closed the sub-nav via Back while on this
  // pathname. We suppress URL-driven auto-open until they navigate away.
  const suppressedPathRef = useRef<string | null>(null)
  // Tracks the pathname we last acted on so the effect below only reacts to
  // actual URL changes — not to state updates from clicking a nav item.
  const lastSyncedPathname = useRef<string | null>(null)

  // Find the currently-open section across both nav groups.
  const allNav = useMemo(() => [...primary, ...secondary], [primary, secondary])
  const openSection = openSectionId
    ? (allNav.find((i) => i.id === openSectionId) ?? null)
    : null

  // URL → sub-nav sync. Only fires when the pathname actually changes, so
  // clicking a nav item doesn't fight against its own state update.
  useEffect(() => {
    if (lastSyncedPathname.current === pathname) return
    lastSyncedPathname.current = pathname

    if (suppressedPathRef.current && suppressedPathRef.current !== pathname) {
      suppressedPathRef.current = null
    }
    if (suppressedPathRef.current === pathname) {
      setOpenSectionId(null)
      return
    }

    // Which sub-nav this URL should have open. Pure, in `config.ts`, and
    // tested there: the rule is "longest matching href across every
    // descendant wins", and getting the descendant half wrong closed the
    // panel somebody was working in.
    const nextOpenId = openSectionIdForPath(pathname, allNav, ctx)
    setOpenSectionId((current) =>
      current === nextOpenId ? current : nextOpenId
    )
  }, [pathname, ctx, allNav])

  function drillInto(item: NavItem) {
    // Drilling into a section only opens its sub-nav — it does NOT navigate to
    // the section's route. The user picks a destination from the children.
    trigger([{ duration: 8 }, { delay: 30, duration: 14 }])
    suppressedPathRef.current = null
    setOpenSectionId(item.id)
  }

  function closeSubNav() {
    trigger([{ duration: 14 }, { delay: 30, duration: 8 }])
    suppressedPathRef.current = pathname
    setOpenSectionId(null)
  }

  return (
    <Sidebar
      collapsible="offcanvas"
      variant="sidebar"
      // The seam is the container's own `border-r`, so there is nothing to kill
      // here now. This used to carry `group-data-[side=left]:border-r-0` because
      // the SidebarResizer painted a resting hairline at the same spot and the
      // two stacked into a doubled line. The resizer no longer paints at rest
      // (it colours on hover/drag only), and a real element border is snapped to
      // the device pixel grid, so it holds a crisp 1px at ANY sidebar width —
      // which the resizer's half-pixel-centred pseudo-element did not once an
      // operator had dragged the rail.
    >
      {/* Header is a fixed h-14 bar — the same height as the content column's
          top nav bar — so the drilled-in back below it lines up with back rows
          in nested panels (e.g. the workflows left panel). `min-h-0` stops the
          flex default `min-height:auto` from letting the org-switcher content
          grow the bar past 56px. */}
      <SidebarHeader className="h-14 min-h-0 justify-center">
        {/* Nudged up so the org picker's baseline lines up with the top-bar
            venue picker (which sits a touch higher). `pr-2` pulls the close X
            in to line up with the nav-row chevrons (which are inset by the
            row's own px-3). */}
        <div className="flex -translate-y-[4.5px] items-center gap-1 pr-2">
          <div className="min-w-0 flex-1">{header}</div>
          {/* Explicit close control so the sidebar can be collapsed without
              hunting for the drag-to-resize edge. */}
          <button
            type="button"
            onClick={() => {
              trigger([{ duration: 12 }])
              toggleSidebar()
            }}
            aria-label="Close navigation"
            title="Close navigation"
            className="inline-flex size-7 flex-none items-center justify-center rounded-full text-[var(--muted-foreground)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-[var(--foreground)]"
          >
            <ChevronsLeft className="size-4" />
          </button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {openSection ? (
          <SubNav
            section={openSection}
            ctx={ctx}
            badges={badges}
            onBack={closeSubNav}
          />
        ) : (
          <>
            {/* Near-zero top padding so the first nav item sits tight under the
                org picker — same fix as the drilled-in sub-nav. */}
            <SidebarGroup className="pt-px">
              <SidebarGroupContent>
                <NavList
                  items={primary}
                  ctx={ctx}
                  badges={badges}
                  onDrillInto={drillInto}
                />
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="h-px w-auto -my-1 bg-border" />
            <SidebarGroup>
              <SidebarGroupContent>
                <NavList
                  items={secondary}
                  ctx={ctx}
                  badges={badges}
                  onDrillInto={drillInto}
                />
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        {/* Persistent update entry — stays here whenever a new build is waiting
            (even after "Later" on the banner) so the update is never lost. */}
        {updateAvailable ? (
          <button
            type="button"
            onClick={applyUpdate}
            disabled={applyingUpdate}
            className="group/update flex w-full items-center gap-2.5 rounded-xl border border-[var(--border)] bg-[var(--muted)] px-2.5 py-2 text-left transition-colors hover:bg-[var(--accent)] disabled:opacity-70"
          >
            <span className="relative flex size-7 shrink-0 items-center justify-center rounded-lg bg-[var(--info)]/10 text-[var(--info)]">
              {applyingUpdate ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {!applyingUpdate ? (
                <span className="absolute -right-0.5 -top-0.5 flex size-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--info)]/70" />
                  <span className="relative inline-flex size-2 rounded-full bg-[var(--info)]" />
                </span>
              ) : null}
            </span>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-xs font-semibold text-[var(--foreground)]">
                {applyingUpdate ? 'Updating…' : 'Update available'}
              </span>
              <span className="block truncate text-[11px] text-[var(--muted-foreground)]">
                Refresh to get the latest
              </span>
            </span>
            {!applyingUpdate ? (
              <RefreshCw className="size-3.5 shrink-0 text-[var(--muted-foreground)] transition-colors group-hover/update:text-[var(--foreground)]" />
            ) : null}
          </button>
        ) : null}
        {footer}
      </SidebarFooter>
      {/* SidebarResizer = drag-to-resize, click-without-drag toggles. */}
      <SidebarResizer />
    </Sidebar>
  )
}
